import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ListWrapper from "../utils/ListWrapper";
import ScrollView2 from "./ScrollView2";
import TextView from "./TextView";
import AuditRecordsRequest from "../models/AuditRecordsRequest";
import AuditRecords from "../classes/AuditRecords";
import SortedHeader from "./SortedHeader";
import AuditRecord from "../models/AuditRecord";
import LeadUtils from "../classes/LeadUtils";
import CollectionUtils from "../utils/CollectionUtils";
import PaginationView from "./PaginationView";
import MessageDispatch from "../rocket/MessageDispatch";
import Query from "../classes/Query";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface AuditLogPageProps extends BaseUIProps {
  key?: string;
}

class _AuditLogPageState extends ObservableComponent<AuditLogPageProps> {
  columnScrollController: ui.ScrollController = new ui.ScrollController();
  request: AuditRecordsRequest = null;
  auditRecords: AuditRecords = null;
  auditLogs: Array<AuditRecord> = ListWrapper.widget(this, "auditLogs");
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AuditLogPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.ascending",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
      ],
      this.computeAuditRecords
    );

    this.computeAuditRecords();

    this.on(["auditRecords", "auditRecords.items"], this.computeAuditLogs);

    this.computeAuditLogs();

    this.on(
      [
        "auditLogs",
        "auditLogs.action",
        "auditLogs.actionDescription",
        "auditLogs.actionOwner",
        "auditLogs.actionOwner.firstName",
        "auditLogs.actionOwner.lastName",
        "auditLogs.createdDate",
        "auditRecords",
        "auditRecords.count",
        "request",
        "request.ascending",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
      ],
      this.rebuild
    );
  }
  public setRequest(val: AuditRecordsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setAuditRecords(val: AuditRecords): void {
    let isValChanged: boolean = this.auditRecords !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("auditRecords", this.auditRecords, val);

    MessageDispatch.get().dispose(this.auditRecords);

    this.auditRecords = val;

    this.fire("auditRecords", this);
  }
  public computeAuditRecords = async (): Promise<void> => {
    try {
      this.setAuditRecords(
        await Query.get().getAuditRecords(
          UsageConstants.QUERY_GETAUDITRECORDS_AUDITLOGPAGE_PROPERTIES_AUDITRECORDS_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeAuditRecords : " + exception.toString()
      );

      this.setAuditRecords(null);
    }
  };
  public setAuditLogs(val: Array<AuditRecord>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.auditLogs,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("auditLogs", this.auditLogs, val);

    this.auditLogs.clear();

    this.auditLogs.addAll(val);

    this.fire("auditLogs", this);
  }
  public addToAuditLogs(val: AuditRecord, index: number = -1): void {
    if (index === -1) {
      if (!this.auditLogs.contains(val)) this.auditLogs.add(val);
    } else {
      this.auditLogs.remove(this.auditLogs.elementAt(index));

      this.auditLogs.add(val);
    }

    this.fire("auditLogs", this, val, true);

    this.updateObservable("auditLogs", null, val);
  }
  public removeFromAuditLogs(val: AuditRecord): void {
    this.auditLogs.remove(val);

    this.fire("auditLogs", this, val, false);

    this.removeObservable("auditLogs", val);
  }
  public computeAuditLogs = (): void => {
    try {
      this.setAuditLogs(
        Array.from(
          this.auditRecords.items.isNotEmpty ? this.auditRecords.items : []
        )
      );
    } catch (exception) {
      console.log(" exception in computeAuditLogs : " + exception.toString());

      this.setAuditLogs([]);
    }
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        children: [
          ui.Table({
            defaultColumnWidth: new ui.FlexColumnWidth(1),
            columnWidths: {
              0: new ui.FlexColumnWidth(120),
              1: new ui.FlexColumnWidth(120),
              2: new ui.FlexColumnWidth(120),
              3: new ui.FlexColumnWidth(1),
            },
            children: [
              ui.TableRow({
                children: [
                  ui.TableCell({
                    child: SortedHeader({
                      name: "Created Date",
                      assending: this.request.ascending,
                      sorted: this.request.orderBy === "createdDate",
                      className: "x3a hc h",
                    }),
                    className: "table-column x8d7 hc h",
                    key: "0",
                  }),
                  ui.TableCell({
                    child: TextView({ data: "Action Owner", className: "hc" }),
                    className: "table-column x13 hc",
                    key: "1",
                  }),
                  ui.TableCell({
                    child: TextView({ data: "Action", className: "hc" }),
                    className: "table-column x9d hc",
                    key: "2",
                  }),
                  ui.TableCell({
                    child: TextView({ data: "Description", className: "hc" }),
                    className: "table-column x3e1 hc",
                    key: "3",
                  }),
                ],
                className: "table-row xd02 hc h",
                key: "0",
              }),
              this.auditLogs.expand((forItem) => [
                ui.TableRow({
                  children: [
                    ui.TableCell({
                      verticalAlignment: ui.TableCellVerticalAlignment.middle,
                      child: TextView({
                        data:
                          forItem.createdDate !== null
                            ? LeadUtils.toGMTFromDateTime(forItem.createdDate, {
                                format: "dd EEE, MMM y HH:mm",
                              })
                            : "",
                        className: "hc",
                      }),
                      className: "table-column hc",
                      key: "0",
                    }),
                    ui.TableCell({
                      verticalAlignment: ui.TableCellVerticalAlignment.middle,
                      child: TextView({
                        data:
                          forItem.actionOwner.firstName +
                            " " +
                            forItem.actionOwner.lastName ?? "",
                        className: "hc",
                      }),
                      className: "table-column hc",
                      key: "1",
                    }),
                    ui.TableCell({
                      verticalAlignment: ui.TableCellVerticalAlignment.middle,
                      child: TextView({
                        data: forItem.action,
                        className: "hc",
                      }),
                      className: "table-column hc",
                      key: "2",
                    }),
                    ui.TableCell({
                      verticalAlignment: ui.TableCellVerticalAlignment.middle,
                      child: TextView({
                        data: forItem.actionDescription,
                        className: "hc",
                      }),
                      className: "table-column hc",
                      key: "3",
                    }),
                  ],
                  className: "table-row xad9 hc",
                  key: forItem?.ident,
                }),
              ]),
            ],
            className: "x25e hc h",
            key: "0",
          }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.end,
            children: [
              PaginationView({
                pageSize: this.request.pageSize,
                total: this.auditRecords?.count ?? 0,
                offset: this.request.offset,
                key: "0",
              }),
            ],
            className: "xb65 hc h",
            key: "1",
          }),
        ],
        className: ui.join("AuditLogPage x4ff hc vc ", this.props.className),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "AuditLogPage x4ff hc vc",
      controller: this.columnScrollController,
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new AuditRecordsRequest({ ascending: false, pageSize: 50, offset: 0 })
    );
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.auditRecords);

    super.dispose();
  }
}
export default function AuditLogPage(props: AuditLogPageProps) {
  return React.createElement(_AuditLogPageState, props);
}
